import React, {
  useCallback,
  useEffect,
  memo,
  useContext,
  useState,
} from 'react';
import Select from 'react-select';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import { Event, SocketContext } from '../../socketProvider';

import OverlayDropdown from './OverlayDropdown';
import { customSelectStylesOptions, socketEventNames } from './helper';
import {
  updateClonedValuesAfterSave,
  updatePath,
  updatePaths,
  updatePosition,
} from './actions';
import { getCameraStatus } from '../../helpers/api';
import { confirmationDialog } from '../../helpers/swal';
import camMap from './mapLogic';
import { emptyMapGridEditingState, formatMapGridCorner } from './mapGridGeometry';

import style from './style.module.scss';

const formatCorner = (corner, position) => ({
  position, // Assuming position is always a valid string input
  row: corner.row, // Already a string, so just pass it through
  seat: Number.parseInt(corner.seat, 10), // Convert seat to an integer
  pan: Number.parseInt(corner.pan, 10), // Convert pan to an integer
  tilt: Number.parseInt(corner.tilt, 10), // Convert tilt to an integer
});
const cornerLabels = ['top_left', 'top_right', 'bottom_left', 'bottom_right'];

const { ROVER_JOIN_LIVESTREAM, ROVER_LEAVE_LIVESTREAM, ROVER_GET_POSITION } = socketEventNames;

const CameraConnect = () => {
  const socket = useContext(SocketContext);
  const dispatch = useDispatch();
  const update = useCallback((...args) => dispatch(updatePath(...args)), []);
  const availableCameras = useSelector(({ mapping: { availableCameras: cameras } }) => cameras);
  const selectedCamera = useSelector(({ mapping: { preview } }) => preview.camera);
  const connectedCamera = useSelector(({ mapping: { connectedCamera: cam } }) => cam);
  const [saveBtnVariant, setSaveBtnVariant] = useState('outline-primary');
  const [isSaveBtnLoading, setIsSaveBtnLoading] = useState(false);
  const currentTab = useSelector(({ mapping: { selectedTab } }) => selectedTab);
  const zoom = useSelector(({ mapping }) => (mapping.preview.zoom.value));
  const angle = useSelector(({ mapping }) => (mapping.preview.angle.value));
  const cornerChecked = useSelector(({ mapping }) => (mapping.check.cornerChecked));
  const userEmail = useSelector(({ authentication }) => (authentication.userData?.username));

  const mapSectionName = useSelector(({ mapping }) => (mapping.map.name));
  const mapType = useSelector(({ mapping }) => (mapping.map.type));
  const mapTopLeft = useSelector(({ mapping }) => (mapping.map.topLeft));
  const mapTopRight = useSelector(({ mapping }) => (mapping.map.topRight));
  const mapBottomLeft = useSelector(({ mapping }) => (mapping.map.bottomLeft));
  const mapBottomRight = useSelector(({ mapping }) => (mapping.map.bottomRight));

  const checkSelectedIndex = useSelector(({ mapping }) => (mapping.check.selectedRegion.index));
  const checkSectionName = useSelector(({ mapping }) => (mapping.check.sectionName));
  const checkType = useSelector(({ mapping }) => (mapping.check.type));
  const originalSectionName = useSelector(({ mapping }) => (mapping.check.originalSectionName));
  const originalType = useSelector(({ mapping }) => (mapping.check.originalType));
  const checkTopLeft = useSelector(({ mapping }) => (mapping.check.topLeft));
  const checkTopRight = useSelector(({ mapping }) => (mapping.check.topRight));
  const checkBottomLeft = useSelector(({ mapping }) => (mapping.check.bottomLeft));
  const checkBottomRight = useSelector(({ mapping }) => (mapping.check.bottomRight));

  const mapGridSectionName = useSelector(({ mapping }) => (mapping.mapGrid.sectionName));
  const mapGridType = useSelector(({ mapping }) => (mapping.mapGrid.type));
  const mapGridSelectedIndex = useSelector(({ mapping }) => (mapping.mapGrid.selectedRegion.index));
  const mapGridOriginalSectionName = useSelector(
    ({ mapping }) => (mapping.mapGrid.originalSectionName),
  );
  const mapGridOriginalType = useSelector(({ mapping }) => (mapping.mapGrid.originalType));
  const mapGridTopLeft = useSelector(({ mapping }) => (mapping.mapGrid.topLeft));
  const mapGridTopRight = useSelector(({ mapping }) => (mapping.mapGrid.topRight));
  const mapGridBottomLeft = useSelector(({ mapping }) => (mapping.mapGrid.bottomLeft));
  const mapGridBottomRight = useSelector(({ mapping }) => (mapping.mapGrid.bottomRight));
  const mapGridZoom = useSelector(({ mapping }) => (mapping.mapGrid.grid?.zoom));
  const mapGridGrid = useSelector(({ mapping }) => (mapping.mapGrid.grid));

  const clearMapCorners = () => {
    const emptyCorner = {
      pan: '', tilt: '', row: '', seat: '',
    };
    dispatch(updatePaths({
      'map.topLeft': { ...emptyCorner },
      'map.topRight': { ...emptyCorner },
      'map.bottomLeft': { ...emptyCorner },
      'map.bottomRight': { ...emptyCorner },
      'map.regionType': 'default',
      'map.type': 'section',
      'map.name': '',
      'preview.angle.value': 0,
    }));
  };

  /** Keeps the mosaic so several regions can be mapped from one scan. */
  const clearMapGridCorners = () => {
    dispatch(updatePaths(emptyMapGridEditingState()));
  };

  const saveBtnHandler = useCallback(async () => {
    setIsSaveBtnLoading(true);
    let result = false;
    let topLeftCorner;
    let topRightCorner;
    let bottomLeftCorner;
    let bottomRightCorner;
    const rotationAngle = Number.parseFloat(angle);
    const zoomFactor = Number.parseInt(zoom, 10) / 1000;

    if (currentTab === 'map' && connectedCamera) {
      topLeftCorner = formatCorner(mapTopLeft, cornerLabels[0]);
      topRightCorner = formatCorner(mapTopRight, cornerLabels[1]);
      bottomLeftCorner = formatCorner(mapBottomLeft, cornerLabels[2]);
      bottomRightCorner = formatCorner(mapBottomRight, cornerLabels[3]);

      result = await camMap.addRegion(
        mapSectionName?.trim(),
        mapType?.trim().toLowerCase(),
        topLeftCorner,
        topRightCorner,
        bottomLeftCorner,
        bottomRightCorner,
        rotationAngle,
        zoomFactor,
      );
      if (result) {
        clearMapCorners();
      }
    } else if (currentTab === 'check' && connectedCamera) {
      topLeftCorner = formatCorner(checkTopLeft, cornerLabels[0]);
      topRightCorner = formatCorner(checkTopRight, cornerLabels[1]);
      bottomLeftCorner = formatCorner(checkBottomLeft, cornerLabels[2]);
      bottomRightCorner = formatCorner(checkBottomRight, cornerLabels[3]);

      const mapperName = cornerChecked && userEmail ? userEmail.split('@')[0] : '';
      const sectionName = checkSectionName?.trim() ?? '';
      const sectionType = checkType?.trim().toLowerCase() ?? '';

      const nameChanged = (originalSectionName?.trim().toLowerCase() ?? '')
        !== (sectionName?.trim().toLowerCase() ?? '');
      const typeChanged = (originalType?.trim().toLowerCase() ?? '')
        !== (sectionType?.trim().toLowerCase() ?? '');

      let proceedWithSave = true;
      if (nameChanged || typeChanged) {
        const confirmResult = await confirmationDialog({
          title: 'Update Section Name/Type',
          text: 'Changing the section name or type will update all regions in this section. Continue?',
        });
        if (!confirmResult.value) proceedWithSave = false;
      }

      if (proceedWithSave) {
        result = await camMap.updateRegion(
          checkSelectedIndex,
          sectionName,
          sectionType,
          topLeftCorner,
          topRightCorner,
          bottomLeftCorner,
          bottomRightCorner,
          rotationAngle,
          zoomFactor,
          cornerChecked,
          mapperName,
        );

        if (result) {
          dispatch(updatePaths({
            'check.originalSectionName': sectionName,
            'check.originalType': sectionType,
          }));
          dispatch(updateClonedValuesAfterSave());
        }
      }
    } else if (currentTab === 'mapGrid' && connectedCamera) {
      const sectionName = mapGridSectionName?.trim() ?? '';
      const sectionType = mapGridType?.trim().toLowerCase() ?? '';
      // Regions inherit the mosaic's capture zoom; the mosaic is never rotated.
      const mosaicZoomFactor = mapGridZoom != null
        ? Math.round(Number(mapGridZoom) * 1e6) / 1e6
        : zoomFactor;
      const mosaicAngle = 0;
      const existingRegion = (mapGridSelectedIndex != null
        && mapGridOriginalSectionName && mapGridOriginalType)
        ? camMap.getRegion(
          mapGridSelectedIndex,
          mapGridOriginalSectionName,
          mapGridOriginalType,
        )
        : null;

      if (existingRegion) {
        const reference = camMap.getReference() || { pan: 0, tilt: 0 };
        const fmtCorner = (c, label) => formatMapGridCorner(c, label, mapGridGrid, reference);
        topLeftCorner = fmtCorner(mapGridTopLeft, cornerLabels[0]);
        topRightCorner = fmtCorner(mapGridTopRight, cornerLabels[1]);
        bottomLeftCorner = fmtCorner(mapGridBottomLeft, cornerLabels[2]);
        bottomRightCorner = fmtCorner(mapGridBottomRight, cornerLabels[3]);

        const existingMeta = existingRegion[4] || {};
        const nameChanged = (mapGridOriginalSectionName?.trim().toLowerCase() ?? '') !== sectionName;
        const typeChanged = (mapGridOriginalType?.trim().toLowerCase() ?? '') !== sectionType;

        let proceedWithSave = true;
        if (nameChanged || typeChanged) {
          const confirmResult = await confirmationDialog({
            title: 'Update Section Name/Type',
            text: 'Changing the section name or type will update all regions in this section. Continue?',
          });
          if (!confirmResult.value) proceedWithSave = false;
        }

        if (proceedWithSave) {
          result = await camMap.updateRegion(
            mapGridSelectedIndex,
            sectionName,
            sectionType,
            topLeftCorner,
            topRightCorner,
            bottomLeftCorner,
            bottomRightCorner,
            mosaicAngle,
            mosaicZoomFactor,
            Boolean(existingMeta.corner_checked),
            existingMeta.mapper_name || '',
          );

          if (result) {
            dispatch(updatePaths({
              'mapGrid.originalSectionName': sectionName,
              'mapGrid.originalType': sectionType,
            }));
            dispatch(updateClonedValuesAfterSave());
          }
        }
      } else {
        // New region: addRegion subtracts the reference itself, and mosaic
        // corners are already relative to it, so hand it absolute positions.
        const reference = camMap.getReference() || { pan: 0, tilt: 0 };
        const toAbsolute = (corner, label) => {
          const formatted = formatMapGridCorner(corner, label, mapGridGrid, reference);
          return {
            ...formatted,
            pan: formatted.pan + Number(reference.pan || 0),
            tilt: formatted.tilt + Number(reference.tilt || 0),
          };
        };

        result = await camMap.addRegion(
          sectionName,
          sectionType,
          toAbsolute(mapGridTopLeft, cornerLabels[0]),
          toAbsolute(mapGridTopRight, cornerLabels[1]),
          toAbsolute(mapGridBottomLeft, cornerLabels[2]),
          toAbsolute(mapGridBottomRight, cornerLabels[3]),
          mosaicAngle,
          mosaicZoomFactor,
        );

        if (result) {
          clearMapGridCorners();
        }
      }
    }

    setSaveBtnVariant(result ? 'success' : 'danger');
    setTimeout(() => {
      setSaveBtnVariant('outline-primary');
      setIsSaveBtnLoading(false);
    }, 1000);
  });

  useEffect(() => {
    if (availableCameras.length === 0) {
      getCameraStatus().then((data) => {
        update('availableCameras', Object.keys(data).map((item) => ({ label: item, value: item })));
      }).catch((e) => console.error('Failed to fetch available cameras', e));
    }
  }, [availableCameras]);

  useEffect(() => {
    if (!selectedCamera && availableCameras.length > 0) {
      const cam1 = availableCameras.find((cam) => cam.value === 'cam1');
      if (cam1) {
        update('preview.camera', cam1);
      }
    }
  }, [availableCameras, selectedCamera, update]);

  const disconnectHandler = useCallback(() => {
    if (connectedCamera) {
      socket.emit(ROVER_LEAVE_LIVESTREAM, 'map', connectedCamera, (disconnected) => {
        if (disconnected) {
          update('connectedCamera', null);
          update('preview.hanwhaClickToMove', false);
        }
      });
    }
  }, [connectedCamera, socket, update]);

  // Cleanup effect to disconnect camera when unmounting
  useEffect(() => () => {
    if (connectedCamera) {
      disconnectHandler();
    }
  }, [disconnectHandler, connectedCamera]);


  useEffect(() => {
    const handleBeforeUnload = () => {
      disconnectHandler();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [disconnectHandler]);

  const connectHandler = useCallback(() => {
    if (!selectedCamera?.value) {
      return;
    }

    socket.emit(ROVER_JOIN_LIVESTREAM, 'map', selectedCamera.value, (connected) => {
      if (connected) {
        update('connectedCamera', selectedCamera.value);
        socket.emit(ROVER_GET_POSITION, 'host', selectedCamera.value, '#');
      }
    });
  }, [selectedCamera, socket, update]);

  const positionHandler = ({ data, error }) => {
    if (error) {
      console.error('Error while getting position on connect:', error);
    }
    if (data) {
      const [p, t, z] = data.split(',');
      // z is total zoom (optical + digital) from backend, already includes digital zoom
      // Multiply by 1000 to match ONVIF_ZOOM_MULTIPLIER format for storage
      const totalZoom = Number.parseFloat(z) * 1000;
      const reference = camMap.getReference();
      dispatch(updatePosition(p, t, totalZoom, reference));
    }
  };

  return (
    <div className={style.cameraConnect}>
      <Event event="position" handler={positionHandler} />
      <div>
        <Select
          placeholder="Select Camera"
          value={selectedCamera}
          options={availableCameras}
          styles={customSelectStylesOptions}
          isDisabled={Boolean(connectedCamera)}
          onChange={(selected) => update('preview.camera', selected)}
        />
        <Button
          variant={connectedCamera ? 'outline-danger' : 'outline-primary'}
          className="small"
          onClick={connectedCamera ? disconnectHandler : connectHandler}
        >
          {connectedCamera ? 'Disconnect' : 'Connect'}
        </Button>
      </div>
      <OverlayDropdown />
      <Button
        variant={saveBtnVariant}
        onClick={saveBtnHandler}
        className="small"
        disabled={isSaveBtnLoading}
      >
        Save
      </Button>
    </div>
  );
};

export default memo(CameraConnect);
